import { useMemo, useState, useCallback } from 'react';
import { ChangeAlertCallbackType } from '../../../Root';
import { crmAfbData, crmAfbInfo } from '../../../../util/DataObject';
import { useCRMFunction } from './useCRMFunction';
import { CRMLeadDetailProps } from '../types';
import { IconButton, Tooltip } from '@mui/material';
import { useDialog } from "@albo-ui/core";
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility';

import moment from 'moment';
moment.locale('es');

export const useCRMTable = (changeAlert: ChangeAlertCallbackType, changeWidgets, config) => {
    const { handleLeadData, handleAgentList, handleLeadByUuid, handleCatalog, handleRevenue, onHandleGroupUpdate, onHandleLeadUpdate, onHandleGroupDelete } = useCRMFunction(changeAlert);

	const [ctFunnel, setCtFunnel] = useState(null);
	const [segments, setSegments] = useState(null);
    const [companyGroups, setCompanyGroups] = useState(null); 
    const [agentList, setAgentList] = useState(null);
    const [data, setData] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [platform, setPlatform] = useState('');
    const [platformData, setPlatformData] = useState(null);
    const [platformInfo, setPlatformInfo] = useState(null);
    const [form, setForm] = useState({
        filters: {
            status_exclude: ["sql", "mql"],
            dates: {
                start: moment().startOf('day').subtract(30, "days").toDate().toISOString().substring(0, 10),
                end: moment().utc().endOf('day').toDate().toISOString().substring(0, 10)
            },
        }
    });
    const [formFind, setFormFind] = useState({
        filters: { keyword: '', type: '' }
    });

	const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalDocuments, setTotalDocuments] = useState(0);

    const [tabActive, setTabActive] = useState('contact');
    const [selectedTab, setSelectedTab] = useState(null);
    const tabs = [
        { name: 'contact', title: 'Leads Inbound', type: "crmInbound" },
        { name: 'contact', title: 'Leads Outbound', type: "crmOutbound" },
        { name: 'company', title: 'Deals' },
        { name: 'company_group', title: 'Grupos Empresariales' },
    ];

    const [showDetail, setShowDetail] = useState(false);
    const [groupDetail, setGroupDetail] = useState(null);
    const [showMenu, setShowMenu] = useState(false);

    const dialogConfirm = useDialog();
    const [row, setRow] = useState(null);
    const [dialogText, setDialogText] = useState({});

    const onConfirmDelete = (row) => {
        setRow(row);
        setDialogText({
            title: "Eliminar registro",
            message: `¿Está seguro que desea eliminar el siguiente Grupo Empresarial? Recuerda que se eliminará de todos los Deals asociados.`,
        });
        dialogConfirm.openDialog();
    };

    const onConfirmDialog = async () => {
        dialogConfirm.onClose();

        setLoading(true);
        let payload = {
            uuid: row.uuid
        }
        if(row.partner_list.length > 0){
            payload['partner_list'] = row.partner_list
        }

        let resp = await onHandleGroupDelete(payload);
        if(resp){
            setRow(null);
            return crmFilter();
        }
        setLoading(false);
    };

    const onCloseDialog = () => {
        dialogConfirm.onClose();
        setRow(null);
    };

    const openMenu = (row?) => {
        setShowMenu(true);
        setGroupDetail(row);
    };
    const closeMenu = () => {
        setShowMenu(false);
        setGroupDetail(null);
    };

    const saveData = async (result) => {
        let validate = Array.isArray(result?.content);
        setData(JSON.parse(JSON.stringify(validate ? result.content || [] : [result.content])));;
        setTotalDocuments(result.pagination ? result.pagination['total_documents'] : 0);
    };

    const saveRequest = async (request) => {
        setPlatform(JSON.parse(JSON.stringify('afb')));
        getCatalog(crmAfbData['platformStatus']);
        setPlatformData(crmAfbData);
        setPlatformInfo(crmAfbInfo);
        await getAgentList('afb');

        tabActive === 'company' && await onGetCompanyGroup();
        tabActive === 'contact' && await getCatalog('customer_segments', [{ 'metadata.external.origin': 'afb' }])
    };
    
    const getCatalog = async (type: string, metadata?: Array<any>) => {
        let resp = await handleCatalog(type, metadata);
        if(resp){
            type === 'customer_segments' ? setSegments(resp) : setCtFunnel(resp);
        }
        setLoading(false);
    };

    const getData = async (newPage = page, newPerPage = rowsPerPage, form, tab, type = platform) => {
        setLoading(true);
        let searchType = formFind['filters']['keyword'] ? 'Find' : 'List'
        let service = tab === 'contact' ? `leadContactCrm${searchType}` : `leadCrm${searchType}`;
        switch (tab) {
            case 'contact':
                service = `leadContactCrm${searchType}`
                break;
            case 'company':
                service = `leadCrm${searchType}`
                break;
            case 'company_group':
                service = `leadCrmGroup${searchType}`
                break;
            default:
                break;
        }

        let f = {};
        if(searchType === 'List'){
            f = form;
            f['pagination'] = {page_number: newPage, results_per_page: newPerPage};
            setForm(JSON.parse(JSON.stringify(f)));
        } else {
            f = formFind
            f['pagination'] = {page_number: newPage, results_per_page: newPerPage};
            form['filters']['status'] && (f['filters']['status'] = form['filters']['status'])

            setFormFind(JSON.parse(JSON.stringify(f)));
        }

        if (tab ===  "company_group") {
            delete f["filters"]["dates"]
        }

        let resp = await handleLeadData(f, service);
        resp ? saveData(resp) : saveData({ content: [] });
        setLoading(false);
    };

    const getRevenue = async () => {
        setLoading(true);
        let resp = await handleRevenue(platformData ? platformData : crmAfbData);
        resp ? setRevenueData(resp || [] ) : setRevenueData([]);
        setLoading(false);
    };

    const onGetCompanyGroup = async () => {
        setLoading(true);
        let request = { filters: {all: true} };
        let resp = await handleLeadData(request, 'leadCrmGroupList');
        resp ? setCompanyGroups(resp['content'] || [] ) : setCompanyGroups([]);
        setLoading(false);
    };

    const getLeadByUuid = async (uuid, tab, crmType= platform, data= platformData, info= platformInfo) => {
        setLoading(true);
        let resp = await handleLeadByUuid(uuid, tab);
        if(resp){
            return handleSectionChange(resp, tab, agentList, platform ? platform : crmType, data, info, selectedTab)
        } else {
            saveData({ content: [] });
        }
        setLoading(false);
    };

    const getAgentList = async (crmType) => {
        setLoading(true);
        let resp = await handleAgentList(crmType);
        if(resp){
            return setAgentList(JSON.parse(JSON.stringify(resp)));
        }
        setLoading(false);
    };

    const crmFilter = () => {
        getData(1, rowsPerPage, form, tabActive);
    };
    
    const changeTab = (tab: string, storedForm?, type?) => {
        setTabActive(tab);
        setData([]);
        clearInput();
        let f = formFind;

        switch (tab) {
            case 'contact':
                f['filters']['type'] = 'email';
                form['filters']['crmContactFlow'] = type;
                setSelectedTab(JSON.parse(JSON.stringify(type)));
                setFormFind(JSON.parse(JSON.stringify(f)));
                delete form['status'];
                return getData(1, rowsPerPage, storedForm ? storedForm : form, tab);
            case 'company':
                f['filters']['type'] = 'email';
                setFormFind(JSON.parse(JSON.stringify(f)));
                getRevenue();
                onGetCompanyGroup();
                return getData(1, rowsPerPage, storedForm ? storedForm : form, tab);
            case 'company_group':
                f['filters']['type'] = 'group_name';
                setFormFind(JSON.parse(JSON.stringify(f)));
                delete form['filters']['status_exclude'];
                return getData(1, rowsPerPage, storedForm ? storedForm : form, tab);
            default:
                break;
        }
    };

    const onHandleChange = (key1, key2, e) => {
        let f = form;
        let value = e.target.value;

        if(key2){
            f['filters'][key1][key2] = value;
        } else if(key1 && value){
            f['filters'][key1] = value;
        } else if(key1 && !value){
            delete f['filters'][key1];
        }
        if(key1 === "status" && value){
            delete form['filters']['status_exclude']
        } else {
            form['filters']['status_exclude'] =  ["sql", "mql"]
        }
        setForm(JSON.parse(JSON.stringify(f)));
    };

    const onHandleChangeFind = (key1, key2, e) => {
        let f = formFind;
        let value = e.target.value;

        if(f['filters']['type'] === 'email'){
            value = value.trim();
        }
        if(key1 === "type"){
            f = {filters: {
                keyword: '',
                type: ''
            }}
        }
        if(key2 && value){
            f['filters'][key1] ? f['filters'][key1][key2] = value : f['filters'][key1] = {[key2]: value};
        } else if(key1 && value){
            f['filters'][key1] = value;
        } else if(key1 && !value){
            delete f['filters'][key1];
        }

        setFormFind(JSON.parse(JSON.stringify(f)));
    };

    const handleSectionChange = useCallback(
        async (lead: CRMLeadDetailProps, tab, agentList, crmType, data, info, crmContactFlow?) => {
            platformData ? platformData["ctFunnel"] = ctFunnel : data["ctFunnel"] = ctFunnel
            const widget = {
                type: 'leadCRMDetail',
                data: lead,
                config: config,
                crmType: platform ? platform : crmType,
                selectedSection: tab,
                platformData: platformData ? platformData : data,
                platformInfo: platformInfo ? platformInfo : info,
                forms: {form: form, formFind: formFind},
                agentList: agentList,
                segments: segments
            };

            if (crmContactFlow) {
                widget['crmContactFlow'] = crmContactFlow;
            }
            if (companyGroups) {
                widget['companyGroups'] = companyGroups;
            }
            return changeWidgets([widget]);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [changeWidgets, config, platform]
    );

    const changePage = (action, page, rows= rowsPerPage) => {
		setPage(page+1);
		getData(page+1, rows,  form, tabActive);
	};
	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(JSON.parse(JSON.stringify(event.target.value)));
        changePage(null, 0, parseInt(event.target.value, 10));
	};

    const clearInput = () => {
        let f = formFind;
        delete formFind['filters']['keyword'];
        delete formFind['filters']['type'];
        setFormFind(JSON.parse(JSON.stringify(f)));
    };

    const checkStatus = (row) => {
        let p = platformData['contactStatus'].filter((item) => row['status'] === item.key)
        if(p?.length === 1){
            return <Chip label={p[0].label} sx={{ bgcolor: p[0]["bgcolor"], color: p[0]["color"], fontWeight: 700,
                '& .MuiChip-label': {whiteSpace: 'normal', textAlign: "center"},
            }} />            
        } else {
            return "-"
        }
    }

    const onHandleDetail = (row) => {
        setGroupDetail(row);
        setShowDetail(true);
    };
    const onCloseDetail = () => {
        setGroupDetail(null);
        setShowDetail(false);
    };
    const onDeleteCompany = async (row) => {
        let g = groupDetail
        let filterList = g["partner_list"].filter((i) => i.uuid !== row.uuid);
        let request = {
            "partner_list": filterList,
            "uuid": g.uuid
        };
        
        setLoading(true);
        onCloseDetail();

        let resp = await onHandleGroupUpdate(request);
        if(resp){
            let payload = {
                uuid: row.uuid,
                charge_fee: 0,
                general: {company_group: ""}
            }
            let respLead = await onHandleLeadUpdate(payload);
            if(respLead){
                setLoading(false);
                return changeTab('company_group');
            }
        }

    };
    

    const calculateColumns = useMemo(() => {
        const columns = [];

        tabActive === 'company' &&
        columns.push(
            {   cell: (row) => {
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    return <a href='#' onClick={getLeadByUuid.bind(this, row['uuid'], tabActive)} title='Ver detalle'>{row['general']['company_name'] || 'Sin nombre'}</a>
                },
                name: 'Nombre de la Empresa', wrap: true, sortable: true, width: '15%'
            },
            { selector: (row) => row['general']['company_phone'] && row['general'].company_phone.phone_code + ' ' + row['general'].company_phone.phone, name: 'Teléfono', wrap: true, sortable: true },
            { selector: (row) => row['general']['onboarding_email'], name: 'Correo', wrap: true, sortable: true },
            { selector: (row) => row['agent']['name'] || "Por asignar", name: 'Agente asignado', wrap: true, sortable: true },
            { cell: (row) => platformData && platformData['companyStatus'].map((item) => row['status'] === item.key ? item.label : null), name: 'Estado del Lead', wrap: true, sortable: true, style: {'& div': {
                width: "128px", display: 'block', whiteSpace: 'normal',
            }}},
            { selector: (row) => (ctFunnel && ctFunnel.map((item) => row['funnel'] === item['v'] ? item['m'][0]['description'] : null)) || 'No aplica', name: `Funnel ${platform}`, wrap: true, sortable: true },
            { selector: (row) => moment(new Date(row['udate'])).tz('America/Mexico_City').format('L'), name: 'Última actividad', wrap: true, sortable: true },
            { selector: (row) => moment(new Date(row['cdate'])).tz('America/Mexico_City').format('L'), name: 'Fecha de creación', wrap: true, sortable: true },
        )
        tabActive === 'contact' &&
        columns.push(
            {   cell: (row) => {
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    return row['general'] && <a href='#' onClick={handleSectionChange.bind(this, row, tabActive, agentList, null, null, null, selectedTab, companyGroups)} title='Ver detalle'>{row['general'].gname + ' ' + row['general'].lname}</a>
                },
                name: 'Contacto principal', wrap: true, sortable: true
            },
            { selector: (row) => row['general'] && row['general'].email, name: 'Correo', wrap: true, sortable: true },
            { selector: (row) => row['general'] && row['general']['phone'] && row['general'].phone.phone_code + ' ' + row['general'].phone.phone, name: 'Teléfono', wrap: true, sortable: true },
            { selector: (row) => (row['agent'] && row['agent']['name']) || "Por asignar", name: 'Agente asignado', wrap: true, sortable: true },
            { selector: (row) => platformData && platformData['contactStatus'] ? checkStatus(row) : "-", name: 'Estatus', wrap: true, sortable: true },
            { selector: (row) => platformData && platformData['origin_platform'].map((item) => row['origin_platform'] === item.key ? item.label : null), name: 'Origen', wrap: true, sortable: true},
            { selector: (row) => row['udate'] === row['cdate'] ? '-' : moment(new Date(row['udate'])).tz('America/Mexico_City').format('L'), name: 'Última actividad', wrap: true, sortable: true, width: "120px" },
            { selector: (row) => moment(new Date(row['cdate'])).tz('America/Mexico_City').format('L'), name: 'Fecha de creación', wrap: true, sortable: true, width: "120px" },
        )
        tabActive === 'company_group' &&
        columns.push(
            { cell: (row) => {
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    return <a href="#" onClick={onHandleDetail.bind(this, row)} title="Ver más"><VisibilityIcon /></a>
                }, name: "", width: "5%", wrap: true, sortable: false
            },
            { selector: (row) => row['group_name'], name: 'Grupo Empresarial', wrap: true, sortable: true, width: "25%" },
            { selector: (row) => row['agent'] && row['agent']['name'], name: 'Agente', wrap: true, sortable: true },
            { selector: (row) => (row['charge_fee'] || "0.0") + "%", name: 'Fee %', wrap: true, sortable: true, width: "120px" },
            { selector: (row) => row['partner_list']?.length, name: 'Deals', wrap: true, sortable: true, width: "120px" },
            { selector: (row) => row['udate'] === row['cdate'] ? '-' : moment(new Date(row['udate'])).tz('America/Mexico_City').format('L'), name: 'Última actividad', wrap: true, sortable: true, width: "120px" },
            { selector: (row) => moment(new Date(row['cdate'])).tz('America/Mexico_City').format('L'), name: 'Fecha de creación', wrap: true, sortable: true, width: "120px" },
            { cell: (item) => {
                    return (
                        <div>
                            <Tooltip title='Editar'>
                                <IconButton onClick={openMenu.bind(this, item)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Eliminar'>
                                <IconButton onClick={onConfirmDelete.bind(this, item)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    );
                },
                name: "Acciones", sortable: true, maxWidth: "15%", center: true
            }
        )
        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActive, ctFunnel, platformData, selectedTab])

    return {
        data,
        form,
        revenueData,
        formFind,
        columns: calculateColumns,
        loading,
        tabActive,
        tabs,
        platformInfo,
        platformData,
        platform,
        ctFunnel,
        agentList,
        pagination: { page, rowsPerPage, totalDocuments},
        showDetail,
        groupDetail,
        showMenu,
        row,
        dialogConfirm,
        dialogText,
        selectedTab,
        segments,
        companyGroups,
        getData,
        saveData,
        saveRequest,
        crmFilter,
        onHandleChange,
        onHandleChangeFind,
        clearInput,
        setTabActive,
        changeTab,
        getLeadByUuid,
        getRevenue,
        changePage,
        handleChangeRowsPerPage,
        onCloseDetail,
        openMenu,
        closeMenu,
        onDeleteCompany,
        onConfirmDialog,
        onCloseDialog,
        setSelectedTab
    };
};
